import { useState, useMemo, useRef, useEffect } from 'react';
import QuestionSkeleton from './QuestionSkeleton';
import { ArrowLeft, Flame, Heart, AlertCircle, Award, Calendar, BookOpen, Clock, ChevronLeft } from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { useShuddhoshikhaContext } from '../../hooks/useShuddhoshikhaContext';
import QuestionCard from './QuestionCard';
import { compulsorySubjects, scienceSubjects, humanitiesSubjects, commerceSubjects, optionalSubjects } from '../../data/syllabus';

interface HistoryViewProps {
  onBack: () => void;
  isDarkMode?: boolean;
}

type TabType = 'favorites' | 'mistakes' | 'exams';



// Slugs mapping functions between database and frontend format
const mapDbSubjectToFrontend = (slug: string): string => {
  const map: Record<string, string> = {
    'bangla': 'sub_bn1',
    'bangla-2nd': 'sub_bn2',
    'english': 'sub_eng1',
    'english-2nd': 'sub_eng2',
    'general-math': 'sub_math',
    'ict': 'sub_ict',
    'religion': 'sub_religion',
    'physics': 'sub_phy',
    'chemistry': 'sub_chem',
    'biology': 'sub_bio',
    'higher-math': 'sub_hmath',
    'history': 'sub_hist',
    'geography': 'sub_geo',
    'civics': 'sub_civ',
    'general-science': 'sub_gsci',
    'accounting': 'sub_acc',
    'finance': 'sub_fin',
    'business': 'sub_bus',
    'economics': 'sub_eco',
    'agriculture': 'opt_agri',
    'home-science': 'opt_home'
  };
  return map[slug] || slug;
};

const mapFrontendSubjectToDb = (id: string): string => {
  const map: Record<string, string> = {
    'sub_bn1': 'bangla',
    'sub_bn2': 'bangla-2nd',
    'sub_eng1': 'english',
    'sub_eng2': 'english-2nd',
    'sub_math': 'general-math',
    'sub_ict': 'ict',
    'sub_religion': 'religion',
    'sub_phy': 'physics',
    'sub_chem': 'chemistry',
    'sub_bio': 'biology',
    'sub_hmath': 'higher-math',
    'sub_hist': 'history',
    'sub_geo': 'geography',
    'sub_civ': 'civics',
    'sub_gsci': 'general-science',
    'sub_acc': 'accounting',
    'sub_fin': 'finance',
    'sub_bus': 'business',
    'sub_eco': 'economics',
    'opt_agri': 'agriculture',
    'opt_home': 'home-science'
  };
  return map[id] || id;
};

const mapDbChapterToFrontend = (subjectSlug: string, chSlug: string): string => {
  if (!chSlug) return 'all';
  if (subjectSlug === 'bangla' || subjectSlug === 'sub_bn1') {
    return chSlug.replace('bangla-ch', 'b1_ch');
  }
  if (subjectSlug === 'bangla-2nd' || subjectSlug === 'sub_bn2') {
    return chSlug.replace('bangla-2nd-ch', 'b2_ch');
  }
  if (subjectSlug === 'english' || subjectSlug === 'sub_eng1') {
    return chSlug.replace('english-ch', 'e1_ch');
  }
  if (subjectSlug === 'english-2nd' || subjectSlug === 'sub_eng2') {
    return chSlug.replace('english-2nd-ch', 'e2_ch');
  }
  if (subjectSlug === 'general-math' || subjectSlug === 'sub_math') {
    return chSlug.replace('general-math-ch', 'm_ch');
  }
  if (subjectSlug === 'ict' || subjectSlug === 'sub_ict') {
    return chSlug.replace('ict-ch', 'ict_ch');
  }
  if (subjectSlug === 'religion' || subjectSlug === 'sub_religion') {
    return chSlug.replace('religion-ch', 'rel_ch');
  }
  if (subjectSlug === 'physics' || subjectSlug === 'sub_phy') {
    return chSlug.replace('phys-ch', 'phy_ch');
  }
  if (subjectSlug === 'chemistry' || subjectSlug === 'sub_chem') {
    return chSlug.replace('chem-ch', 'chem_ch');
  }
  if (subjectSlug === 'biology' || subjectSlug === 'sub_bio') {
    return chSlug.replace('biology-ch', 'bio_ch');
  }
  if (subjectSlug === 'higher-math' || subjectSlug === 'sub_hmath') {
    return chSlug.replace('hmath-ch', 'hm_ch');
  }
  if (subjectSlug === 'history' || subjectSlug === 'sub_hist') {
    return chSlug.replace('history-ch', 'hist_ch');
  }
  if (subjectSlug === 'geography' || subjectSlug === 'sub_geo') {
    return chSlug.replace('geography-ch', 'geo_ch');
  }
  if (subjectSlug === 'civics' || subjectSlug === 'sub_civ') {
    return chSlug.replace('civics-ch', 'civ_ch');
  }
  if (subjectSlug === 'general-science' || subjectSlug === 'sub_gsci') {
    return chSlug.replace('gen-science-ch', 'gs_ch');
  }
  if (subjectSlug === 'accounting' || subjectSlug === 'sub_acc') {
    return chSlug.replace('accounting-ch', 'acc_ch');
  }
  if (subjectSlug === 'finance' || subjectSlug === 'sub_fin') {
    return chSlug.replace('finance-ch', 'fin_ch');
  }
  if (subjectSlug === 'business' || subjectSlug === 'sub_bus') {
    return chSlug.replace('business-ch', 'bus_ch');
  }
  if (subjectSlug === 'economics' || subjectSlug === 'sub_eco') {
    return chSlug.replace('economics-ch', 'eco_ch');
  }
  if (subjectSlug === 'agriculture' || subjectSlug === 'opt_agri') {
    return chSlug.replace('agriculture-ch', 'agr_ch');
  }
  if (subjectSlug === 'home-science' || subjectSlug === 'opt_home') {
    return chSlug.replace('home-science-ch', 'hs_ch');
  }
  return chSlug;
};

// Bengali numbers converter helper
const toBengaliNumber = (num: number | string) => {
  if (num === null || num === undefined) return '০';
  const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().split('').map(digit => {
    const parsed = parseInt(digit);
    return isNaN(parsed) ? digit : bengaliDigits[parsed];
  }).join('');
};

export default function HistoryView({ onBack, isDarkMode }: HistoryViewProps) {
  const shAuth = (window as any).shAuth;
  const { favorites } = useFavorites();
  const { subjects } = useShuddhoshikhaContext();
  const [activeTab, setActiveTab] = useState<TabType>('favorites');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>('all');
  const [selectedChapterId, setSelectedChapterId] = useState<string>('all');
  
  // Real DB history states
  const [dbExams, setDbExams] = useState<any[]>([]);
  const [dbMistakes, setDbMistakes] = useState<any[]>([]);
  const [dbFavorites, setDbFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch attempts, mistakes and favorites dynamically from PDO sync handler
  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const dbSubject = selectedSubjectId !== 'all' ? mapFrontendSubjectToDb(selectedSubjectId) : 'all';
        const url = `/wp-json/shuddhoshikha/v1/sync-history?type=${activeTab}&subject=${dbSubject}`;
        const headers: Record<string, string> = {};
        if (shAuth?.rest_nonce) {
          headers['X-WP-Nonce'] = shAuth.rest_nonce;
        }
        const res = await fetch(url, { headers });
        if (res.ok) {
          const resData = await res.json();
          if (resData.success) {
            if (activeTab === 'exams') {
              setDbExams(resData.data || []);
            } else if (activeTab === 'mistakes') {
              setDbMistakes(resData.data || []);
            } else if (activeTab === 'favorites') {
              setDbFavorites(resData.data || []);
            }
          }
        }
      } catch (err) {
        console.error('Failed to retrieve user history data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [activeTab, selectedSubjectId]);

  // Register global handler for optimistic mistakes deletion
  useEffect(() => {
    (window as any).singleClickDelete = (buttonElement: HTMLButtonElement, questionId: number) => {
      const card = buttonElement.closest('.question-card') as HTMLElement;
      if (!card) return;

      // Disable button instantly to prevent double-clicks
      buttonElement.disabled = true;
      buttonElement.style.pointerEvents = 'none';

      // Keep backup of original styles/classes for potential rollback
      const originalClasses = Array.from(card.classList);
      const originalStyle = card.getAttribute('style') || '';

      // Force explicit height transition
      const rect = card.getBoundingClientRect();
      card.style.maxHeight = rect.height + 'px';
      void card.offsetHeight; // force repaint

      // 1. INSTANT UI UPDATE (Optimistic collapse transition)
      card.classList.add('opacity-0', '-translate-x-full', 'max-h-0', 'p-0', 'm-0', 'overflow-hidden', 'border-none');
      card.style.padding = '0';
      card.style.margin = '0';
      card.style.maxHeight = '0';
      card.style.border = 'none';

      // Toast notification helper
      const showToast = (msg: string) => {
        const toast = document.createElement('div');
        toast.className = 'fixed bottom-5 right-5 bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-xl z-[9999] flex items-center gap-2 border border-rose-500/30 text-sm font-bangla transition-all duration-300 transform translate-y-10 opacity-0';
        toast.innerHTML = `
          <svg class="w-4 h-4 text-white animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <span>${msg}</span>
        `;
        document.body.appendChild(toast);
        setTimeout(() => toast.classList.remove('translate-y-10', 'opacity-0'), 10);
        setTimeout(() => {
          toast.classList.add('translate-y-10', 'opacity-0');
          setTimeout(() => toast.remove(), 300);
        }, 3500);
      };

      // 2. BACKGROUND SYNC PROMISE
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (shAuth?.rest_nonce) {
        headers['X-WP-Nonce'] = shAuth.rest_nonce;
      }

      const fetchPromise = fetch('/wp-json/shuddhoshikha/v1/sync-history', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          action: 'delete_mistake',
          question_id: questionId
        })
      })
      .then(res => {
        if (!res.ok) throw new Error('Network response not ok');
        return res.json();
      })
      .then(data => {
        if (data.status !== 'success') {
          throw new Error(data.message || 'Server rejected deletion');
        }
        return data;
      });

      // 3. TRANSITION TIMER PROMISE (300ms)
      const transitionPromise = new Promise(resolve => setTimeout(resolve, 300));

      // 4. COORDINATE AND COMPLETE (Only update state after transition finishes)
      Promise.all([fetchPromise, transitionPromise])
      .then(() => {
        // Safe: Update local React state, React handles the DOM node removal safely
        setDbMistakes(prev => prev.filter(q => q.id !== questionId));
      })
      .catch(err => {
        console.error('Optimistic delete failed:', err);
        
        // Rollback UI to original state
        buttonElement.disabled = false;
        buttonElement.style.pointerEvents = 'auto';
        card.setAttribute('class', originalClasses.join(' '));
        if (originalStyle) {
          card.setAttribute('style', originalStyle);
        } else {
          card.removeAttribute('style');
        }
        
        showToast('মুছে ফেলা যায়নি, নেটওয়ার্ক চেক করুন।');
      });
    };

    return () => {
      delete (window as any).singleClickDelete;
    };
  }, []);

  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll speed multiplier
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  // fallback list of subjects if API fails/is offline
  const subjectList = useMemo(() => {
    const list = subjects.length > 0 ? subjects : [
      { id: 'sub_bn1', name: 'বাংলা ১ম পত্র' },
      { id: 'sub_bn2', name: 'বাংলা ২য় পত্র' },
      { id: 'sub_eng1', name: 'English 1st paper' },
      { id: 'sub_eng2', name: 'English 2nd paper' },
      { id: 'sub_religion', name: 'ইসলাম ও নৈতিক শিক্ষা' },
      { id: 'sub_math', name: 'গণিত' },
    ];
    return [{ id: 'all', name: 'সব বিষয়' }, ...list];
  }, [subjects]);

  const activeSubjectName = useMemo(() => {
    return subjectList.find(s => s.id === selectedSubjectId)?.name || 'সব বিষয়';
  }, [selectedSubjectId, subjectList]);

  // Find chapters from context or fallback to syllabus
  const chapterList = useMemo(() => {
    if (selectedSubjectId === 'all') return [];

    let chapters: { id: string; name: string; totalQuestions?: number }[] = [];

    // Find in context first
    const contextSubject = subjects.find(s => s.id === selectedSubjectId);
    if (contextSubject && contextSubject.chapters && contextSubject.chapters.length > 0) {
      chapters = contextSubject.chapters;
    } else {
      // Find in syllabus fallback
      const allSyllabusSubjects = [
        ...compulsorySubjects,
        ...scienceSubjects,
        ...humanitiesSubjects,
        ...commerceSubjects,
        ...Object.values(optionalSubjects)
      ];
      const syllabusSubject = allSyllabusSubjects.find(s => s.id === selectedSubjectId);
      if (syllabusSubject && syllabusSubject.chapters && syllabusSubject.chapters.length > 0) {
        chapters = syllabusSubject.chapters;
      }
    }

    // If it is Bangla 1st Paper (sub_bn1), ensure "মমতাদি" and "নিয়তি" are present/added as fallback chapters
    if (selectedSubjectId === 'sub_bn1') {
      const hasMomatadi = chapters.some(c => c.name.includes('মমতাদি'));
      if (!hasMomatadi) {
        chapters = [
          ...chapters,
          { id: 'b1_ch_momatadi', name: 'গদ্য: মমতাদি', totalQuestions: 85 },
          { id: 'b1_ch_niyoti', name: 'গদ্য: নিয়তি', totalQuestions: 90 },
        ];
      }
    }

    return [{ id: 'all', name: 'সব অধ্যায়' }, ...chapters];
  }, [selectedSubjectId, subjects]);

  const handleSelectSubject = (id: string) => {
    setSelectedSubjectId(id);
    setSelectedChapterId('all');
  };

  // Filter items based on activeTab, selectedSubjectId and selectedChapterId
  const filteredFavorites = useMemo(() => {
    // Fall back to local favorites if DB list is empty/offline
    const favoritesList = (dbFavorites && dbFavorites.length > 0) ? dbFavorites : favorites;
    const mapped = favoritesList.map((q) => ({
      ...q,
      subject_id: q.subject_id ? mapDbSubjectToFrontend(q.subject_id) : q.subject_id,
      chapter_id: q.chapter_id ? mapDbChapterToFrontend(q.subject_id, q.chapter_id) : q.chapter_id,
      subject_name: q.subject_id ? (subjectList.find(s => s.id === mapDbSubjectToFrontend(q.subject_id))?.name || q.subject_id) : ''
    }));

    if (selectedSubjectId === 'all') return mapped;
    return mapped.filter(q => {
      const matchSubject = q.subject_id === selectedSubjectId;
      if (!matchSubject) return false;
      if (selectedChapterId === 'all') return true;
      return q.chapter_id === selectedChapterId;
    });
  }, [dbFavorites, favorites, selectedSubjectId, selectedChapterId, subjectList]);

  // Helper to construct a readable attempt title
  const getAttemptTitle = (subjSlug: string, quizType: string): string => {
    if (!subjSlug) return 'চর্চা সেশন';
    const names = subjSlug.split(',').map(slug => {
      const frontendSub = mapDbSubjectToFrontend(slug);
      const subInfo = subjectList.find(s => s.id === frontendSub);
      return subInfo ? subInfo.name : slug;
    });
    const typeSuffix = quizType === 'mock' ? 'মক পরীক্ষা' : 'অধ্যায় কুইজ';
    return `${names.join(', ')} - ${typeSuffix}`;
  };

  // Helper to format created_at date in Bengali
  const formatAttemptDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('bn-BD', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  const filteredMistakes = useMemo(() => {
    const mistakesList = Array.isArray(dbMistakes) ? dbMistakes : [];
    const mapped = mistakesList.map((q) => ({
      ...q,
      subject_id: mapDbSubjectToFrontend(q.subject_id),
      chapter_id: mapDbChapterToFrontend(q.subject_id, q.chapter_id),
      subject_name: subjectList.find(s => s.id === mapDbSubjectToFrontend(q.subject_id))?.name || q.subject_id
    }));

    if (selectedSubjectId === 'all') return mapped;
    return mapped.filter(q => {
      const matchSubject = q.subject_id === selectedSubjectId;
      if (!matchSubject) return false;
      if (selectedChapterId === 'all') return true;
      return q.chapter_id === selectedChapterId;
    });
  }, [dbMistakes, selectedSubjectId, selectedChapterId, subjectList]);

  const filteredExams = useMemo(() => {
    const examsList = Array.isArray(dbExams) ? dbExams : [];
    const mapped = examsList.map(exam => {
      const totalQuestions = exam.total_questions || 0;
      const correctCount = exam.correct_count || 0;
      const accuracy = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      const quizType = 'mock';

      return {
        id: exam.id,
        title: getAttemptTitle(exam.subject_slug, quizType),
        date: formatAttemptDate(exam.completed_at),
        score: `${toBengaliNumber(exam.obtained_score || correctCount)}/${toBengaliNumber(totalQuestions)}`,
        accuracy: `${toBengaliNumber(accuracy)}%`,
        format: 'MCQ',
        subject_slugs: exam.subject_slug ? exam.subject_slug.split(',') : [],
        chapter_id: 'all'
      };
    });

    return mapped;
  }, [dbExams, subjectList]);

  const activeChapterName = useMemo(() => {
    return chapterList.find(c => c.id === selectedChapterId)?.name || '';
  }, [selectedChapterId, chapterList]);

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-150/60 dark:border-white/5 pb-5">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 rounded-2xl bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition-all shadow-sm active:scale-95 cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          <div className="flex items-center gap-2.5">
            <h2 className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">রিভিশন</h2>
            {/* Green Streak badge */}
            <div className="flex items-center gap-1 bg-[#E6F4EA] dark:bg-[#E6F4EA]/10 text-[#137333] dark:text-[#8AB4F8] px-2.5 py-1 rounded-full text-xs font-semibold">
              <Flame className="w-3.5 h-3.5 fill-current" />
              <span className="font-bangla text-xs">২</span>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-gray-100/80 dark:bg-white/5 p-1 rounded-2xl self-start sm:self-auto border border-gray-100/50 dark:border-white/5">
          <button
            onClick={() => setActiveTab('favorites')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bangla font-semibold transition-all cursor-pointer ${
              activeTab === 'favorites'
                ? 'bg-[#137333] text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-[#137333] dark:hover:text-white'
            }`}
          >
            দাগানো প্রশ্ন
          </button>
          <button
            onClick={() => setActiveTab('mistakes')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bangla font-semibold transition-all cursor-pointer ${
              activeTab === 'mistakes'
                ? 'bg-[#137333] text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-[#137333] dark:hover:text-white'
            }`}
          >
            পূর্বের ভুল
          </button>
          <button
            onClick={() => setActiveTab('exams')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bangla font-semibold transition-all cursor-pointer ${
              activeTab === 'exams'
                ? 'bg-[#137333] text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-[#137333] dark:hover:text-white'
            }`}
          >
            পরীক্ষা
          </button>
        </div>
      </div>

      {/* Horizontally scrollable subject/chapter carousel (Hides scrollbar) */}
      <div className="relative">
        {selectedSubjectId === 'all' ? (
          <div 
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide touch-pan-x select-none cursor-grab active:cursor-grabbing"
          >
            {subjectList.map((subject) => {
              const isSelected = selectedSubjectId === subject.id;
              return (
                <button
                  key={subject.id}
                  onClick={() => handleSelectSubject(subject.id)}
                  className={`px-4 py-2.5 rounded-full text-xs font-bangla font-bold whitespace-nowrap transition-all border cursor-pointer ${
                    isSelected
                      ? 'bg-[#137333] border-[#137333] text-white shadow-md'
                      : 'bg-white dark:bg-[#1E293B] border-gray-150 dark:border-white/5 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-white/10 shadow-sm hover:shadow'
                  }`}
                >
                  {subject.name}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-3 relative">
            {/* Back button representing the selected subject */}
            <button 
              onClick={() => {
                setSelectedSubjectId('all');
                setSelectedChapterId('all');
              }}
              className="flex items-center gap-1 text-sm font-bangla font-bold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer shrink-0 py-2.5 bg-transparent border-none"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{activeSubjectName}</span>
            </button>
            
            {/* Vertical divider line */}
            <div className="w-px h-6 bg-gray-200 dark:bg-white/10 shrink-0" />

            {/* Scrollable Chapter carousel */}
            <div className="relative flex-1 min-w-0">
              <div 
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide touch-pan-x select-none cursor-grab active:cursor-grabbing"
              >
                {chapterList.map((chapter) => {
                  const isSelected = selectedChapterId === chapter.id;
                  return (
                    <button
                      key={chapter.id}
                      onClick={() => setSelectedChapterId(chapter.id)}
                      className={`px-4 py-2.5 rounded-full text-xs font-bangla font-bold whitespace-nowrap transition-all border cursor-pointer ${
                        isSelected
                          ? 'bg-[#137333] border-[#137333] text-white shadow-md'
                          : 'bg-white dark:bg-[#1E293B] border-gray-150 dark:border-white/5 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-white/10 shadow-sm hover:shadow'
                      }`}
                    >
                      {chapter.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main List Area */}
      <div className="space-y-4">
        {loading ? (
          activeTab === 'exams' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-white/10 rounded-2xl p-5 flex items-start justify-between">
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="h-3 w-16 skel-shimmer rounded" />
                    <div className="h-4 w-3/4 skel-shimmer rounded mb-1" />
                    <div className="h-3 w-1/2 skel-shimmer rounded" />
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-4">
                    <div className="h-6 w-12 skel-shimmer rounded" />
                    <div className="h-3 w-16 skel-shimmer rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <QuestionSkeleton qType="mcq" count={3} />
          )
        ) : (
          <>
            {/* Tab 1: Favorites */}
            {activeTab === 'favorites' && (
              <div className="space-y-4">
                {filteredFavorites.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-white dark:bg-[#1E293B] rounded-3xl border border-gray-100 dark:border-white/10 p-8 shadow-[0_4px_16px_rgba(0,0,0,0.02)]">
                    <div className="w-16 h-16 rounded-full bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center text-rose-500">
                      <Heart className="w-8 h-8 stroke-[1.5]" />
                    </div>
                    <div className="space-y-1">
                      <h5 className="font-bangla text-base font-bold text-gray-700 dark:text-gray-200">
                        {selectedSubjectId === 'all' 
                          ? 'কোনো প্রশ্ন দাগানো নেই' 
                          : selectedChapterId === 'all' 
                            ? `${activeSubjectName}-এ কোনো দাগানো প্রশ্ন নেই` 
                            : `${activeSubjectName} - ${activeChapterName}-এ কোনো দাগানো প্রশ্ন নেই`}
                      </h5>
                      <p className="font-bangla text-xs text-gray-400 dark:text-gray-500 max-w-sm leading-relaxed">
                        অনুশীলন করার সময় প্রশ্নের ডানদিকের হার্ট আইকন (♡) ক্লিক করে আপনার পছন্দের প্রশ্নগুলো এখানে সংরক্ষণ করতে পারেন।
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {filteredFavorites.map((q, idx) => (
                      <QuestionCard key={q.id} question={q} index={idx} showAnswersByDefault={true} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Mistakes */}
            {activeTab === 'mistakes' && (
              <div className="space-y-4">
                {filteredMistakes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-white dark:bg-[#1E293B] rounded-3xl border border-gray-100 dark:border-white/10 p-8 shadow-[0_4px_16px_rgba(0,0,0,0.02)]">
                    <div className="w-16 h-16 rounded-full bg-[#00CBA9]/10 flex items-center justify-center text-[#00CBA9]">
                      <AlertCircle className="w-8 h-8 stroke-[1.5]" />
                    </div>
                    <div className="space-y-1">
                      <h5 className="font-bangla text-base font-bold text-gray-700 dark:text-gray-200">
                        {selectedSubjectId === 'all' 
                          ? 'কোনো ভুল উত্তর পাওয়া যায়নি' 
                          : selectedChapterId === 'all' 
                            ? `${activeSubjectName}-এ কোনো ভুল উত্তর পাওয়া যায়নি` 
                            : `${activeSubjectName} - ${activeChapterName}-এ কোনো ভুল উত্তর পাওয়া যায়নি`}
                      </h5>
                      <p className="font-bangla text-xs text-gray-400 dark:text-gray-500 max-w-sm leading-relaxed">
                        আপনার পূর্বে ভুল করা সব প্রশ্ন এখানে তালিকাভুক্ত হবে যাতে আপনি সেগুলো পুনরায় অনুশীলন করতে পারেন।
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {filteredMistakes.map((q, idx) => (
                      <QuestionCard key={q.id} question={q} index={idx} showAnswersByDefault={true} showDeleteButton={true} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Exams */}
            {activeTab === 'exams' && (
              <div className="space-y-4">
                {filteredExams.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-white dark:bg-[#1E293B] rounded-3xl border border-gray-100 dark:border-white/10 p-8 shadow-[0_4px_16px_rgba(0,0,0,0.02)]">
                    <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-500">
                      <Award className="w-8 h-8 stroke-[1.5]" />
                    </div>
                    <div className="space-y-1">
                      <h5 className="font-bangla text-base font-bold text-gray-700 dark:text-gray-200">
                        {selectedSubjectId === 'all' 
                          ? 'পরীক্ষার কোনো রেকর্ড নেই' 
                          : selectedChapterId === 'all' 
                            ? `${activeSubjectName}-এ পরীক্ষার কোনো রেকর্ড নেই` 
                            : `${activeSubjectName} - ${activeChapterName}-এ পরীক্ষার কোনো রেকর্ড নেই`}
                      </h5>
                      <p className="font-bangla text-xs text-gray-400 dark:text-gray-500 max-w-sm leading-relaxed">
                        মক পরীক্ষা সম্পন্ন করার পর আপনার ফলাফল ও স্কোর হিস্ট্রি এখানে দেখানো হবে।
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredExams.map((exam) => (
                      <div 
                        key={exam.id}
                        className="bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-white/10 rounded-2xl p-5 flex items-start justify-between shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300"
                      >
                        <div className="space-y-2 text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="font-english text-[9.5px] font-bold tracking-wider px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-[#8AB4F8] uppercase">
                              {exam.format}
                            </span>
                          </div>
                          <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">
                            {exam.title}
                          </h4>
                          <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-[#A0A5AD]">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {exam.date}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <span className="font-english text-xl font-bold text-[#137333] dark:text-[#8AB4F8]">
                            {exam.score}
                          </span>
                          <span className="font-bangla text-[10px] text-gray-400">
                            সঠিকতা: {exam.accuracy}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
